import { useMemo, useState } from "react";
import { Toggle } from "@/components/ui/toggle";
import { createAttributeComponent } from "@coltorapps/builder-react";
import {
  getComponentValidator,
  getRegistryValidators,
  type ValidatorAttributeValue,
  validatorAttribute,
} from "./definition";
import { useValidators } from "@/hooks/useValidator";
import { Validator } from "@/types/validator";
import useValidatorStore from "@/hooks/useValidatorStore";
import { SearchableSelect } from "@ui/select/searchable-select";
import { useDebounce } from "@/hooks/useDebounce";
import CodeEditButton from "@ui/button/code-edit-button";

export const ValidatorAttribute = createAttributeComponent(
  validatorAttribute,
  function ValidationAttribute(props) {
    const [search, setSearch] = useState<string>();
    const debounceSearch = useDebounce(search, 700);
    props.entity.id;
    const { validators } = useValidators({
      name: debounceSearch,
      component: props.entity.type,
    });
    const { setValidators, getValidators } = useValidatorStore();
    const storedValidators = getValidators(props.entity.id);

    const validatorOptions = useMemo(() => {
      const optionsById = new Map(
        [...storedValidators, ...(validators ?? [])].map((validator) => [
          validator.id,
          validator,
        ]),
      );
      return Array.from(optionsById.values()).map((v) => ({
        label: v.name,
        key: v.id,
        value: v.id,
        validator: v,
      }));
    }, [storedValidators, validators]);

    const componentValidator = getComponentValidator(props.attribute.value);
    const registryValidators = getRegistryValidators(props.attribute.value);
    const selectedValidatorIds = registryValidators.map(
      (validator) => validator.validatorId,
    );
    const storedValidatorIds = storedValidators.map((validator) => validator.id);
    const value =
      storedValidatorIds.length > 0 ? storedValidatorIds : selectedValidatorIds;

    function getBaseValue(): ValidatorAttributeValue {
      const validator = {
        ...(props.attribute.value ?? {}),
        required: props.attribute.value?.required ?? false,
      } as ValidatorAttributeValue & Record<string, unknown>;
      delete validator.code;
      delete validator.errorMessage;
      delete validator.isApi;
      delete validator.validatorId;
      return validator;
    }

    return (
      <div className="space-y-3">
        <div className="flex flex-row justify-between">
          <span className="text-dark font-medium text-base pb-2.5">
            Required
          </span>
          <Toggle
            pressed={props.attribute.value?.required ?? false}
            onPressedChange={(value) =>
              props.setValue({
                ...getBaseValue(),
                required: value,
              })
            }
          />
        </div>
        {props.attribute.value?.required && (
          <>
            <div>
              <div className="text-dark font-medium text-base pb-2.5">
                Validation Source
              </div>
              <SearchableSelect
                mode="multiple"
                options={validatorOptions ?? []}
                value={value}
                onChange={(value) => {
                  const selectedValidators =
                    validatorOptions
                      ?.filter((option) => value.includes(option.value))
                      .map((option) => option.validator) ?? [];

                  props.setValue({
                    ...getBaseValue(),
                    registryValidators: value.map((validatorId) => {
                      const existing = registryValidators.find(
                        (validator) => validator.validatorId === validatorId,
                      );
                      return {
                        validatorId,
                        ...(existing?.enableApi !== undefined
                          ? { enableApi: existing.enableApi }
                          : {}),
                      };
                    }),
                  });
                  setValidators(props.entity.id, selectedValidators);
                }}
                placeholder="Select Source"
                onSearchChange={(value) => setSearch(value)}
              />
            </div>
            <div>
              <div className="text-dark font-medium text-base pb-2.5">
                Validation Expression
              </div>
              <CodeEditButton
                variant="validation"
                value={
                  componentValidator?.code ??
                  "function validation(value) {\n return true;\n}"
                }
                isApi={componentValidator?.enableApi}
                showApiToggle
                allowEmptyCode
                trigger={componentValidator?.code}
                onSave={(code, d, e, isApi) => {
                  const trimmedCode = code.trim();
                  const nextComponentValidator = {
                    ...(componentValidator ?? {}),
                    errorMessage: e,
                    enableApi: isApi,
                  };

                  if (trimmedCode) {
                    nextComponentValidator.code = code;
                  } else {
                    delete nextComponentValidator.code;
                  }

                  props.setValue({
                    ...getBaseValue(),
                    componentValidator: nextComponentValidator,
                  });
                }}
              />
            </div>
          </>
        )}
      </div>
    );
  },
);
